import chroma from "chroma-js";
import { head, pipe, clamp, sortBy } from "ramda";

const ASPECT = 9 / 16;
const DEFAULT_COLORS = ["#2697f8", "#fece6f", "#fff", "#f0f", "#0bf", "#000"];
const DEFAULT_CHARACTERS = " .-/|[]1234567890HELP@" // " .,:;i1tfLCG08@"
  .split("")
  .map((x) => (x === " " ? "&nbsp;" : x)); //.reverse().join("");

const DEFAULT_SUBLIMINAL_MESSAGES = [
  "HELP",
  "SOS",
  "WILLTHISBEONTHETEST?",
  "SUBLIMINALMESSAGE",
  "IGNORETHISMESSAGE",
  "THISISNTACLUE"
];

const getSubliminalMessage = () =>
  DEFAULT_SUBLIMINAL_MESSAGES[
    Math.floor(Math.random() * DEFAULT_SUBLIMINAL_MESSAGES.length)
  ];

// calculate contrast factor
// http://www.dfstudios.co.uk/articles/image-processing-algorithms-part-5/
const getContrastFactor = (contrast = 100) =>
  (259 * (contrast + 255)) / (255 * (259 - contrast));

const createGetClosestColors = (colors = DEFAULT_COLORS) => {
  const cache = {};
  return (color) => {
    if (cache[color]) {
      return cache[color];
    }
    const res = pipe(
      sortBy((possibleColor) => chroma.distance(possibleColor, color)),
      head
    )(colors);
    cache[color] = res;
    return res;
  };
};

const getColorAtOffset = (data, offset) => [
  data[offset],
  data[offset + 1],
  data[offset + 2]
  // data[offset + 3] // alpha not needed
];

const getCanvasContext = (width, height) => {
  const CANVAS_ID = "ascii-canvas";
  let canvas = document.getElementById(CANVAS_ID);
  if (!canvas) {
    canvas = document.createElement("canvas");
    canvas.id = CANVAS_ID;
    canvas.style.display = "none";
    document.body.appendChild(canvas);
  }
  canvas.width = width;
  canvas.height = height;
  return [canvas, canvas.getContext("2d")];
};

export const createVideoToAscii = ({
  width = 60,
  height = 60 * ASPECT,
  colors = DEFAULT_COLORS,
  characters = DEFAULT_CHARACTERS,
  contrast = 100
} = {}) => {
  const contrastFactor = getContrastFactor(contrast);
  const getClosestColor = createGetClosestColors(colors);
  const [canvas, ctx] = getCanvasContext(
    clamp(1, Infinity, width),
    clamp(1, Infinity, height)
  );

  return (videoElement) => {
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    var imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const rows = [];
    for (let y = 0; y < canvas.height; y++) {
      let row = "";
      for (let x = 0; x < canvas.width; x++) {
        const offset = (y * canvas.width + x) * 4;
        const color = getColorAtOffset(imageData.data, offset);
        // increase the contrast of the image so that the ASCII representation looks better
        // http://www.dfstudios.co.uk/articles/image-processing-algorithms-part-5/
        const contrastedColor = color.map((x) =>
          clamp(0, 255, Math.floor((x - 128) * contrastFactor) + 128)
        );
        const chromaColor = chroma(contrastedColor);
        let character =
          characters[
            Math.round(chromaColor.luminance() * (characters.length - 1))
          ];

        if ((y === canvas.height - 1 && x === 10) || Math.random() < 0.0001) {
          character = getSubliminalMessage();
          x += character.length - 1;
        }

        row += `<span style="color:${getClosestColor(
          chromaColor.hex()
        )};">${character}</span>`;
      }
      rows.push(row);
    }
    return rows.join("<br />");
  };
};
